import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useAuth } from '@screentone/addon-auth-wrapper';
import { Button, FormHelperText, FormLabel, Group, IconExternalLink, Input, Wrapper } from '@screentone/core';

import { ImageManagerEmbed } from 'components/image-manager-embed/ImageManagerEmbed';
import { SnippetyImageData, SnippetyResponseWithImage, VideoDataForSummarian } from 'hooks/useSnippetyQuery';
import { CHARTS_DOMAIN } from 'utils/constants';
import styles from '../MediaInput.module.scss';
import { MediaTypes } from '../MediaInput.utils';

import { ControlledAltSumm, DEFAULT_MEDIA_ALT_SUMM } from 'components/altsumm-modal/AltSummModal.utils';

type Props = {
  onIdChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onDelete: () => void;
  id: string;
  error: string;
  onIdAdd:
    ((imageData: SnippetyResponseWithImage | null) => void) | ((videoData: VideoDataForSummarian | null) => void);
  isIdFormOpen: boolean;
  setIsIdFormOpen: (isOpen: boolean) => void;
  shouldAdd: boolean;
  inputType: MediaTypes | '';
  setVariant: (variant: MediaTypes | '') => void;
  setControlledAltSum: React.Dispatch<React.SetStateAction<ControlledAltSumm>>;
  viewOnly?: boolean;
};

export const MediaInputForm = ({
  onIdChange,
  onDelete,
  id,
  error,
  onIdAdd,
  isIdFormOpen,
  setIsIdFormOpen,
  shouldAdd,
  setVariant,
  inputType,
  setControlledAltSum,
  viewOnly = false
}: Props) => {
  const onCancel = () => {
    setIsIdFormOpen(false);
    setVariant('');
    onDelete();
    setControlledAltSum((prev) => ({
      ...prev,
      ...DEFAULT_MEDIA_ALT_SUMM
    }));
  };

  const { currentProperty } = useAuth();

  const ref = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isIdFormOpen && ref.current) {
      ref.current.focus();
    }
  }, [isIdFormOpen]);

  const labelInfo = useMemo(() => {
    switch (inputType) {
      case 'image':
        return { formLabel: 'Image ID', inputPlaceholder: 'Image ID' };
      case 'video':
        return { formLabel: 'Video GUID', inputPlaceholder: 'Video GUID' };
      case 'chart':
        return { formLabel: 'Chart ID or dynamic inset URL', inputPlaceholder: 'Chart ID or dynamic inset URL' };
      default:
        return { formLabel: 'Media ID', inputPlaceholder: 'Media ID' };
    }
  }, [inputType]);

  const embedCallback = useCallback(
    (response: SnippetyImageData) => {
      // Image Manager returns the image data, so we can use it to populate the form.
      onIdChange({
        target: {
          value: response.src?.imageId ?? `im-${response.id}`
        }
      } as React.ChangeEvent<HTMLInputElement>);
    },
    [onIdChange]
  );

  return (
    <div data-testid="media-input-form-wrapper">
      {isIdFormOpen && (
        <Wrapper data-testid="media-input-form" padding={{ top: 'lg', right: 'sm', bottom: 'lg', left: 'sm' }}>
          <Group direction="column" gap="none" fullWidth>
            <Group data-testid="media-input-form-container" fullWidth valign="end" gap="xs" wrap={false}>
              <FormLabel data-testid="media-input-form-id" label={labelInfo.formLabel} fullWidth>
                <Input
                  data-testid="media-input-form-media-input"
                  type="text"
                  placeholder={labelInfo.inputPlaceholder}
                  onChange={onIdChange}
                  value={id}
                  error={!!error}
                  componentRef={ref}
                  disabled={viewOnly}
                />
              </FormLabel>
              <Button
                data-testid="media-input-form-add-button"
                primary
                onClick={onIdAdd}
                disabled={!shouldAdd || viewOnly}
                margin={{ left: 'xs' }}
                className={styles.addImageButton}
              >
                Add
              </Button>
            </Group>
            <Group fullWidth align="space-between" valign="end" gap="xs" wrap={false} margin={{ top: 'sm' }}>
              {inputType === 'image' && (
                <FormHelperText className='lc-st_formhelpertext image-action-buttons'>
                  <ImageManagerEmbed disabled={viewOnly} callback={embedCallback} />
                  in Image Manager.
                </FormHelperText>
              )}
              {inputType === 'chart' && (
                <Button
                  tertiary
                  componentEl="a"
                  href={`${CHARTS_DOMAIN}/${currentProperty}/`}
                  target="_blank"
                  rel="noopener noreferrer"
                  icon={IconExternalLink}
                  size="sm"
                >
                  Charts
                </Button>
              )}
              <Button
                tertiary
                // icon={IconTrash}
                color="lava"
                onClick={onCancel}
                size="sm"
                disabled={viewOnly}
              >
                Remove
              </Button>
            </Group>
          </Group>
          {error && (
            <FormHelperText data-testid="media-input-form-error" error>
              {error}
            </FormHelperText>
          )}
        </Wrapper>
      )}
    </div>
  );
};
